import {
  Icon,
  NavSectionProps,
  NavSectionVertical,
  Scrollbar,
  useIsMiniLayout,
} from '@aibees/shared/components';
import { useDebounceFn, useResponsive } from '@aibees/shared/hooks';
import { usePathname } from '@aibees/shared/router';
import { bgBlur } from '@aibees/shared/theme';
import { Box, Drawer, Stack, TextFieldProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { NavHeader } from '../_common';
import { NAV } from '../config-layout';
import { useFilterMenu } from '../hooks/use-filter-menu';
import { StyledSearchInput } from './styles';

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
  options?: NavSectionProps;
};

// Pre-computed width objects (stable references)
const WIDTH_MINI = { lg: NAV.W_MINI, md: NAV.W_MINI };
const WIDTH_FULL = { lg: NAV.W_VERTICAL };

export default memo(function NavVertical({
  openNav,
  onCloseNav,
  options: _options,
}: Props) {
  const pathname = usePathname();
  const mdUp = useResponsive('up', 'md');
  const isMini = useIsMiniLayout();
  const isOpen = !useIsMiniLayout();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const { footer, prepend, ...options } = _options || {};
  const isDark = theme.palette.mode === 'dark';
  const width = isMini ? WIDTH_MINI : WIDTH_FULL;

  // Close nav on pathname change without causing re-render cascades
  const onCloseNavRef = useRef(onCloseNav);
  onCloseNavRef.current = onCloseNav;
  useEffect(() => {
    if (openNav) onCloseNavRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleSearch: TextFieldProps['onChange'] = useDebounceFn((e) => {
    const search = e.target.value;
    setSearchQuery(search);
  }, 300);

  const filterMenu = useFilterMenu(options.data, searchQuery);

  // Memoize sx objects that depend on theme to avoid new references every render
  const scrollbarSx = useMemo(
    () => ({
      height: 1,
      '& .simplebar-content': {
        height: 'auto',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      pb: 3,
      ...options.config?.sx,
    }),
    [options.config?.sx]
  );

  const navBoxSx = useMemo(
    () => ({
      display: mdUp ? 'block' : 'none',
      flexShrink: { lg: 0 },
      width,
      boxSizing: 'content-box' as const,
      position: 'relative' as const,
      borderRight: `1px solid ${
        isDark ? theme.palette.grey[900] : theme.palette.grey[400]
      }`,
      ...bgBlur({
        color: isDark ? theme.palette.grey[800] : theme.palette.common.white,
      }),
      paddingBottom: theme.spacing(1),
      ...options?.sx,
    }),
    [mdUp, width, isDark, theme, options?.sx]
  );

  const searchBoxSx = useMemo(
    () => ({
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(1),
    }),
    [theme]
  );

  const searchAdornment = useMemo(
    () => ({
      startAdornment: <Icon color={'grey.500'} size={20} name={'search'} />,
    }),
    []
  );

  const stackSx = useMemo(
    () => ({
      height: 1,
      width: width.lg,
    }),
    [width.lg]
  );

  const drawerPaperSx = useMemo(
    () => ({
      width: width.lg,
    }),
    [width.lg]
  );

  const renderContent = (
    <Scrollbar sx={scrollbarSx}>
      {options.config?.searchable && (
        <StyledSearchInput
          autoComplete="off"
          variant="outlined"
          size="small"
          placeholder="Search for Settings..."
          onChange={handleSearch}
          InputProps={searchAdornment}
        />
      )}
      <NavSectionVertical {...options} data={filterMenu} />
      {prepend}
    </Scrollbar>
  );

  return (
    <Box component="nav" sx={navBoxSx}>
      {mdUp ? (
        <Stack sx={stackSx}>
          {isOpen && options.header?.beforeElement}
          {isOpen && options.header?.searchElement && (
            <Box sx={searchBoxSx}>{options.header?.searchElement}</Box>
          )}
          {options?.header && <NavHeader {...options.header} />}
          {renderContent}
          <Box>{footer}</Box>
        </Stack>
      ) : (
        <Drawer open={openNav} onClose={onCloseNav} PaperProps={drawerPaperSx}>
          {options?.header && (
            <NavHeader {...(options.header as any)} closable={false} />
          )}
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
});
